import {
  Attachment,
  AttachmentMetadata,
  Chat,
  ChatRecap,
  CustomerMessage,
  CustomerMessageSubtype,
  PersistentMessage,
} from "@/types/chat"
import { InspirationRef, PlaceRef } from "@/types/entity"
import { TripLogItem, TripRef, TripUpdate } from "@/types/trip"

import { BaseApi } from "@/lib/base-api"
import { wait, withTimeout } from "./promise-utils"
import { ChatBotVersionKey } from "./storage/ui-settings-slice"

export class ChatApi extends BaseApi {
  // Create a new chat. Optionally pass trip parameters to set up an
  // initial trip, or pass a tripRef to associate the chat with an
  // existing trip. Don't pass both.
  //
  // If trip or kickoffContext is passed -- or initiateResponse is true -- the bot
  // will begin the conversation, so the caller should initiate streaming
  // if the new chat has stream ids. Prevent this by passing false in
  // initiateResponse.
  async createChat(
    trip?: TripUpdate,
    tripRef?: TripRef,
    initiateResponse?: boolean,
    options?: {
      kickoffContext?: string
      entityRefs?: string[]
    }
  ) {
    return await this.post<{
      chat: Chat
      count: number
      deactivated_chat_ids: number[]
    }>(`/api/chats`, {
      trip,
      trip_ref: tripRef,
      initiate_response: initiateResponse,
      kickoff_context: options?.kickoffContext,
      entity_refs: options?.entityRefs,
    })
  }

  async startServerChat(actionPillRef: string) {
    return await this.post<{
      chat: Chat
      count: number
      deactivated_chat_ids: number[]
    }>(`/api/chats/start_server_chat`, { action_pill_ref: actionPillRef })
  }

  async createChatWithMessage(messageId: number) {
    return await this.post<{
      chat: Chat
      count: number
      deactivated_chat_ids: number[]
    }>(`/api/chats/create_with_message`, { message_id: messageId })
  }

  // Returns the chat with the given ID. If the ID is "latest", then
  // the chat with the most recent message is returned, or a new chat
  // if the user doesn't have any chats.
  async fetchChat(id: number | "latest"): Promise<Chat> {
    return (await this.get<{ chat: Chat }>(`/api/chats/${id}`)).chat
  }

  async authorizeMessageStream(chatId: number, streamId: string) {
    await this.head(
      `/api/chats/${chatId}/authorize_stream/${encodeURIComponent(streamId)}`
    )
  }

  async deactivateChat(id: number): Promise<void> {
    await this.delete(`/api/chats/${id}`)
  }

  async updateChat(id: number, updates: { name?: string }) {
    return await this.put<{ chat: Chat }>(`/api/chats/${id}`, {
      chat: updates,
    })
  }

  async setChatMemory(id: number, memoryEnabled: boolean) {
    return await this.put<{ chat: Chat }>(`/api/chats/${id}/memory`, {
      memory_enabled: memoryEnabled,
    })
  }

  async convertToGroupChat(id: number) {
    return await this.put<{ chat: Chat }>(
      `/api/chats/${id}/change-to-group-chat`,
      {}
    )
  }

  async moveChatToTrip(id: number, tripRef: TripRef) {
    return await this.put<{ chat: Chat }>(`/api/chats/${id}/move-to-trip`, {
      trip_ref: tripRef,
    })
  }

  async removeChatFromTrip(id: number) {
    return await this.put<{ chat: Chat }>(
      `/api/chats/${id}/remove-from-trip`,
      {}
    )
  }

  async fetchMessages(
    chatId: number,
    options?: { beforeId?: number; afterId?: number }
  ) {
    let url = `/api/chats/${chatId}/messages?`
    if (options?.beforeId) {
      url += `before_id=${options.beforeId}&`
    }
    if (options?.afterId) {
      url += `after_id=${options.afterId}`
    }
    return await this.get<{
      messages: PersistentMessage[]
      log_items: TripLogItem[]
      more: boolean
    }>(url)
  }

  async fetchRecap(chatId: number) {
    const response = await this.get<undefined | { recap: ChatRecap }>(
      `/api/chats/${chatId}/recap`
    )
    return response?.recap
  }

  // Creates a customer message and opens a channel for listening to
  // the bot's forthcoming response, if any.
  async addCustomerMessage(
    chatId: number,
    body: string,
    options?: {
      bot?: ChatBotVersionKey
      hint?: string
      attachments?: Attachment[]
      locationOfInterest?: PlaceRef
      entityRef?: PlaceRef | null
      entityRefs?: string[]
      subType?: CustomerMessageSubtype
    }
  ) {
    const data = await this.post<{
      stream_id?: string
      customer_message: CustomerMessage
      redirect_to_chat_id?: number
      chat?: Chat
    }>(`/api/chats/${chatId}/message`, {
      message_body: body,
      bot: options?.bot || "default",
      hint: options?.hint,
      attachments: options?.attachments,
      location_of_interest: options?.locationOfInterest,
      entity_ref: options?.entityRef,
      entity_refs: options?.entityRefs,
      sub_type: options?.subType,
    })

    return {
      stream_id: data.stream_id,
      message: data.customer_message,
      redirect_to_chat_id: data.redirect_to_chat_id,
      chat: data.chat,
    }
  }

  async updateCustomerMessage(
    chatId: number,
    messageId: number,
    body: string,
    chatBotVersion: ChatBotVersionKey,
    hint?: string,
    attachments?: Attachment[]
  ) {
    const data = await this.put<{
      stream_id?: string
      customer_message: CustomerMessage
      title: string
    }>(`/api/chats/${chatId}/message/${messageId}`, {
      message_body: body,
      message_id: messageId,
      bot: chatBotVersion,
      attachments,
    })

    return {
      stream_id: data.stream_id,
      message: data.customer_message,
      title: data.title,
    }
  }

  async addMessageFromInspirationDMO(
    chatId: number,
    inspirationRef: InspirationRef,
    chatBotVersion?: ChatBotVersionKey,
    message?: string,
    options?: {
      subType?: CustomerMessageSubtype
    }
  ) {
    const data = await this.post<{
      stream_id: string
      customer_message: CustomerMessage
    }>(`/api/chats/${chatId}/message-from-inspiration-dmo`, {
      inspiration_ref: inspirationRef,
      message_body: message,
      bot: chatBotVersion,
      sub_type: options?.subType,
    })

    return {
      stream_id: data.stream_id,
      message: data.customer_message,
    }
  }

  async addMessageFromInspiration(
    chatId: number,
    inspirationRef: InspirationRef,
    chatBotVersion?: ChatBotVersionKey,
    message?: string,
    options?: {
      subType?: CustomerMessageSubtype
    }
  ) {
    const data = await this.post<{
      stream_id: string
      customer_message: CustomerMessage
    }>(`/api/chats/${chatId}/message-from-inspiration`, {
      inspiration_ref: inspirationRef,
      message_body: message,
      bot: chatBotVersion,
      sub_type: options?.subType,
    })

    return {
      stream_id: data.stream_id,
      message: data.customer_message,
    }
  }

  // Ask the bot to begin streaming a follow-up message.
  // Server auto-detects the follow-up type (events, inspiration, quiz, or regular)
  async createFollowUp(chatId: number) {
    return await this.post<{ stream_id: string }>(
      `/api/chats/${chatId}/follow-up`
    )
  }

  async initialBotGreeting(chatId: number, geoHint?: string) {
    return await this.post<{ stream_id?: string }>(
      `/api/chats/${chatId}/initial-bot-greeting`,
      { geo_hint: geoHint }
    )
  }

  async restartBotResponse(chatId: number, chatBotVersion: ChatBotVersionKey) {
    return await this.post<{
      messages?: PersistentMessage[]
      stream_id?: string
    }>(`/api/chats/${chatId}/restart_bot_response`, {
      chatId,
      bot: chatBotVersion,
    })
  }

  async interruptTool(chatId: number, sessionId: number) {
    return await this.post<void>(`/api/chats/${chatId}/interrupt_tool`, {
      session_id: sessionId,
    })
  }

  async stopResponse(chatId: number) {
    return await this.post<void>(`/api/chats/${chatId}/stop_response`, {})
  }

  async fetchAttachmentMetadata(url: string) {
    const MAX_ATTEMPTS = 3

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        return await withTimeout(
          this.get<AttachmentMetadata>(`/api/attachment_metadata`, {
            params: { url: url.trim() },
          }),
          10_000,
          "timed out"
        )
      } catch (error) {
        if (error instanceof Error && error.message === "timed out") {
          console.error("Request timed out:", error)
          if (attempt < MAX_ATTEMPTS) {
            const backoff = Math.min(2 ** (attempt - 1) * 250, 2000)
            const jitter = Math.floor(Math.random() * 150)
            await wait(backoff + jitter)
            continue // retry
          } else {
            throw error
          }
        }
      }
    }

    throw new Error("Failed to fetch attachment metadata after retries")
  }

  async upload(
    file: File,
    target?: { chatId: number } | { tripRef: TripRef }
  ): Promise<Attachment> {
    const formData = new FormData()
    formData.append("files[0]", file)
    if (target) {
      if ("chatId" in target) {
        formData.append("chat_id", String(target.chatId))
      } else {
        formData.append("trip_ref", target.tripRef)
      }
    }
    return await this.post("/api/chats/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  }

  async assignCommission(chatId: number) {
    return await this.post(`/api/chats/${chatId}/assign_commission`)
  }

  async createItinerary(chatId: number, message?: string) {
    const data = await this.post<{
      stream_id?: string
      customer_message: CustomerMessage
    }>(`/api/chats/${chatId}/itinerary`, { message })

    return {
      stream_id: data.stream_id,
      message: data.customer_message,
    }
  }
}
